import { useEffect, useRef, useState } from 'react';
import type { Chat } from '../lib/api';
import { loadManifest, mimeForMember, tailMessages } from '../lib/archive';
import { Viewer, type Shown } from './Viewer';

type Tab = 'media' | 'docs' | 'links';

interface Entry {
  kind: Tab;
  ts: number;
  chatId: string;
  who?: string;
  /** Media and docs. */
  cid?: string;
  mime?: string;
  bytes?: number;
  /** Links. */
  url?: string;
}

/** Bounded so eight lanes of lookups cannot become eight hundred. */
const LANES = 8;
const URL_RE = /https?:\/\/[^\s<>"')]+/gi;

const size = (n?: number) =>
  n === undefined ? ''
  : n < 1024 ** 2 ? `${(n / 1024).toFixed(0)} KB`
  : `${(n / 1024 ** 2).toFixed(1)} MB`;

const day = (ts: number) => {
  const d = new Date(ts);
  const today = new Date();
  const diff = Math.floor((+new Date(today.toDateString()) - +new Date(d.toDateString())) / 86400000);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * What is in the archive that is not words: pictures and video, documents,
 * and the links people sent each other — across every conversation.
 *
 * The first version read every conversation whole and resolved the type of
 * every attachment before drawing anything. On a real archive that is the
 * entire thing decrypted, on a phone, to fill a grid nobody scrolls to the end
 * of — slow enough to look broken, and it grew the local manifest by every
 * chunk in the account while it went.
 *
 * This reads the tail instead. A directory lists its chunks in order, so the
 * recent end of a conversation is its last chunk: one fetch each, merged and
 * sorted, newest first. "Show older" asks every conversation for one chunk
 * more. Nothing is written to the device beyond the chunk geometry that
 * reading requires, and nothing is read that the screen does not use.
 */
export function MediaPanel({ accountKey, chats, titleOf, onOpenChat, onPrime, active }: {
  accountKey: Uint8Array;
  chats: Chat[];
  titleOf: (c: Chat) => string;
  onOpenChat: (c: Chat) => void;
  /** New members were learned; the Service Worker needs them to serve media. */
  onPrime: () => void;
  /**
   * The conversation being read, if any.
   *
   * Opening Media from inside a conversation means the photos of *that*
   * conversation — the whole archive is the answer to a question nobody asked
   * while looking at one chat, and it is also far more to read.
   */
  active: Chat | null;
}) {
  const [tab, setTab] = useState<Tab>('media');
  const [entries, setEntries] = useState<Entry[]>([]);
  const [depth, setDepth] = useState(1);
  const [busy, setBusy] = useState(true);
  const [more, setMore] = useState(false);
  /** Índice na lista de mídia que está aberto em tela cheia, se algum. */
  const [viewing, setViewing] = useState<number | null>(null);
  const live = useRef(true);
  /** Within the open conversation until asked otherwise, and reset by opening
   *  a different one — the scope follows what is being read. */
  const [onlyActive, setOnlyActive] = useState(true);
  useEffect(() => { setOnlyActive(true); }, [active?.chatId]);

  const scoped = active !== null && onlyActive;
  const reading = scoped ? [active!] : chats;
  const key = reading.map((c) => c.chatId).join(',');

  useEffect(() => {
    live.current = true;
    setBusy(true);
    // Cleared, not left standing. Switching from one conversation to the
    // whole archive kept the previous tiles on screen under the new heading,
    // so for a moment the panel said "From all chats" over one chat's photos.
    setEntries([]);
    setViewing(null);

    void (async () => {
      const found: Entry[] = [];
      let older = false;

      for (const c of reading) {
        if (!live.current) return;
        if (!c.directoryCid) continue;
        try {
          const { messages, more: hasMore } = await tailMessages(accountKey, c.directoryCid, depth);
          if (hasMore) older = true;
          for (const m of messages) {
            if (m.body) {
              for (const url of m.body.match(URL_RE) ?? []) {
                found.push({ kind: 'links', ts: m.ts, chatId: c.chatId, who: m.sender, url });
              }
            }
            if (m.attach) {
              found.push({ kind: 'media', ts: m.ts, chatId: c.chatId, who: m.sender, cid: m.attach });
            }
          }
        } catch { /* a conversation that will not open is not this panel's problem */ }
      }
      if (!live.current) return;

      // Types, for the attachments only, and only the ones the local manifest
      // cannot already answer. Eight at a time rather than one after another.
      const manifest = loadManifest();
      const attachments = found.filter((e) => e.cid);
      for (const e of attachments) {
        const known = manifest[e.cid!];
        if (known) { e.mime = known.mime; e.bytes = known.plainLength; }
      }
      const unknown = attachments.filter((e) => !e.mime);
      let next = 0;
      await Promise.all(Array.from({ length: Math.min(LANES, unknown.length) }, async () => {
        for (;;) {
          const e = unknown[next++];
          if (!e || !live.current) return;
          e.mime = await mimeForMember(e.cid!).catch(() => 'application/octet-stream');
          e.bytes = loadManifest()[e.cid!]?.plainLength;
        }
      }));
      if (!live.current) return;

      // A document is an attachment that is not a picture, a video or a voice
      // note — the same split WhatsApp shows under its own Docs tab.
      for (const e of attachments) {
        const m = e.mime ?? '';
        if (!m.startsWith('image/') && !m.startsWith('video/')) e.kind = 'docs';
        if (m.startsWith('audio/')) e.kind = 'audio' as Tab;   // voice notes live in the chat
      }

      // The Service Worker serves /media/<id> from its own copy of the
      // manifest. Without this the tiles for anything learned just now are
      // broken images — which is what "failed to fetch" was.
      onPrime();

      setEntries(found.filter((e) => e.kind !== ('audio' as Tab))
                      .sort((a, b) => b.ts - a.ts));
      setMore(older);
      setBusy(false);
    })();

    return () => { live.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, accountKey, depth]);

  const chatOf = (id: string) => chats.find((c) => c.chatId === id);
  const shown = entries.filter((e) => e.kind === tab);
  const count = (t: Tab) => entries.filter((e) => e.kind === t).length;

  // A mesma lista que a grade mostra, para as setas andarem por ela.
  const gallery: Shown[] = tab === 'media'
    ? shown.map((e) => ({ cid: e.cid!, mime: e.mime ?? 'image/jpeg', ts: e.ts, who: e.who,
                          chat: chatOf(e.chatId) ? titleOf(chatOf(e.chatId)!) : '' }))
    : [];

  return (
    <div className="mediapanel">
      {viewing !== null && gallery[viewing] && (
        <Viewer items={gallery} at={viewing} onAt={setViewing}
                onClose={() => setViewing(null)}
                onOpenChat={() => {
                  const c = chatOf(shown[viewing]!.chatId);
                  setViewing(null);
                  if (c) onOpenChat(c);
                }} />
      )}
      <header>
        <strong>Media</strong>
        <span className="fine">
          {scoped ? titleOf(active!) : 'From all chats'}{busy ? ' · reading…' : ''}
          {scoped && (
            <> · <button className="link" onClick={() => setOnlyActive(false)}>
              All chats</button></>
          )}
          {!scoped && active && (
            <> · <button className="link" onClick={() => setOnlyActive(true)}>
              Only {titleOf(active)}</button></>
          )}
        </span>
      </header>

      <div className="tabs" role="tablist">
        {(['media', 'docs', 'links'] as Tab[]).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t}
                  className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t === 'media' ? 'Media' : t === 'docs' ? 'Docs' : 'Links'}
            {!busy && <span className="n">{count(t)}</span>}
          </button>
        ))}
      </div>

      <div className="scroll">
        {!busy && shown.length === 0 && (
          <p className="fine empty">
            {tab === 'media' ? 'No photos or video in the recent part of your conversations.'
             : tab === 'docs' ? 'No documents in the recent part of your conversations.'
             : 'No links in the recent part of your conversations.'}
            {more && ' Show older to look further back.'}
          </p>
        )}

        {tab === 'media' && (
          <div className="grid">
            {shown.map((e, i) => {
              const c = chatOf(e.chatId);
              const prev = shown[i - 1];
              const head = !prev || day(prev.ts) !== day(e.ts);
              const video = (e.mime ?? '').startsWith('video/');
              return (
                <div key={`${e.cid}-${e.ts}-${i}`} className="slot">
                  {head && <h4 className="whenhead">{day(e.ts)}</h4>}
                  <button className="cell" title={c ? titleOf(c) : ''}
                          onClick={() => setViewing(i)}>
                    {video
                      ? <><video src={`/media/${e.cid}`} preload="metadata" muted />
                          <span className="kind" aria-hidden="true">▶</span></>
                      : <img src={`/media/${e.cid}`} alt="" loading="lazy" />}
                    <span className="from">{e.who ?? (c ? titleOf(c) : '')}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {tab !== 'media' && (
          <ul className="rows">
            {shown.map((e, i) => {
              const c = chatOf(e.chatId);
              return (
                <li key={`${e.url ?? e.cid}-${e.ts}-${i}`}>
                  <button className="rowlink" onClick={() => { if (c) onOpenChat(c); }}>
                    <span className="mark" aria-hidden="true">{tab === 'docs' ? '▤' : '🔗'}</span>
                    <span className="what">
                      <strong>{tab === 'docs' ? `Document · ${size(e.bytes)}` : e.url}</strong>
                      <span className="fine">
                        {e.who ? `${e.who} · ` : ''}{c ? titleOf(c) : ''} · {day(e.ts)}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {more && (
          <div className="older">
            <button className="ghost" disabled={busy} onClick={() => setDepth((d) => d + 1)}>
              {busy ? 'Reading…' : 'Show older'}
            </button>
            <p className="fine">Reads one more chunk from every conversation.</p>
          </div>
        )}
      </div>
    </div>
  );
}
